import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  InputAdornment
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { api } from '../../utils/api';
import { format } from 'date-fns';
import SaleReceiptPrint from './SaleReceiptPrint';
import PrintIcon from '@mui/icons-material/Print';

const CompleteSaleDialog = ({ open, onClose, sale, onCompleted }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    companyName: 'FreeWheel',
    companyAddress: '',
    companyCity: '',
    companyPostalCode: '',
    companyProvince: '',
    companyPhone: '',
    companyEmail: '',
    companyVatNumber: ''
  });
  const [printData, setPrintData] = useState({ 
    paymentMethodId: '', 
    paymentAmount: 0, 
    paymentDate: format(new Date(), 'yyyy-MM-dd')
  });

  // Formatta un importo a due cifre decimali
  const formatAmount = (amount) => {
    return Number(amount).toFixed(2);
  };

  // Calcola il totale dalla somma degli articoli
  const calculateTotal = () => {
    const items = sale?.SaleItems || sale?.items || [];
    return items.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price || item.unitPrice || 0);
      const quantity = parseInt(item.quantity || 0);
      return sum + (itemPrice * quantity);
    }, 0);
  };

  const [receiptNumber, setReceiptNumber] = useState(null);

  // Funzione per generare un numero di ricevuta reale dal backend
  const generateRealReceiptNumber = async () => {
    try {
      const response = await api.get('/sales/generate-receipt-number');
      return response.data.receiptNumber;
    } catch (error) {
      console.error('Errore nella generazione del numero ricevuta:', error);
      // Fallback locale se il backend non risponde
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const counter = Math.floor(Date.now() / 1000) % 10000;
      return `${dateStr}-${counter.toString().padStart(4, '0')}`;
    }
  };

  // Determina il numero di ricevuta da mostrare
  const getReceiptNumber = () => {
    if (sale?.saleNumber) {
      return `${sale.saleNumber} (ID: ${sale.id})`; // Mostra sia il numero reale che l'ID
    } else if (sale?.id) {
      return `RIC-${sale.id}`; // Fallback al vecchio formato se disponibile
    } else if (receiptNumber) {
      return `${receiptNumber} (ID: da assegnare)`; // Mostra il numero generato con placeholder per ID
    } else {
      return 'Generando...'; // Mostra loading
    }
  };

  // Genera il numero di ricevuta quando il componente si monta
  useEffect(() => {
    if (!sale?.id && !sale?.saleNumber) {
      generateRealReceiptNumber().then(setReceiptNumber);
    }
  }, [sale]);

  const prepareSaleData = () => {
    // Assicuriamoci che i dati di vendita siano formattati correttamente
    const items = sale?.items || [];
    console.log('Preparazione vendita da sale:', sale);
    return {
      ...sale,
      items: items.map(item => {
        console.log('Preparazione item:', item);
        // Gestisci gli articoli personalizzati
        const isCustomItem =
          item.isCustom ||
          (item.productId && item.productId.toString().startsWith('ART-PER-')) ||
          (!item.productId && (parseFloat(item.purchasePrice) || 0) > 0);

        if (isCustomItem) {
          console.log('Rilevato articolo personalizzato:', item);
          const customItem = {
            code: item.code,
            description: item.description || 'Articolo personalizzato',
            quantity: parseInt(item.quantity) || 1,
            price: parseFloat(item.price) || 0,
            total: parseFloat(item.total) || parseFloat(item.price) * (parseInt(item.quantity) || 1),
            productId: item.productId,
            isCustom: true,
            purchasePrice: parseFloat(item.purchasePrice) || 0
          };
          console.log('Item personalizzato formattato:', customItem);
          return customItem;
        }
        // Articoli normali del catalogo
        return {
          productId: item.productId,
          code: item.code,
          description: item.description,
          quantity: parseInt(item.quantity) || 1,
          price: parseFloat(item.price) || 0,
          total: parseFloat(item.total) || parseFloat(item.price) * (parseInt(item.quantity) || 1),
          includeLabor: !!item.includeLabor
        };
      })
    };
  };

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      paymentAmount: 0,
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      paymentMethodId: '',
      accountId: ''
    }
  });

  // Carica i dati dell'azienda
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        console.log('DEBUG CompleteSaleDialog - Caricamento impostazioni aziendali...');
        // Prima prova direttamente con /company-settings che è l'endpoint corretto
        let response = await api.get('/company-settings');
        
        if (!response.data || Object.keys(response.data).length === 0) {
          // Se non ci sono dati, prova con /settings come fallback
          console.log('DEBUG CompleteSaleDialog - Nessun dato trovato in /company-settings, provo con /settings');
          response = await api.get('/settings');
        }
        
        if (response.data) {
          const settings = {};
          
          // Mappa le impostazioni
          Object.entries(response.data).forEach(([key, value]) => {
            settings[key] = value;
          });
          
          console.log('DEBUG CompleteSaleDialog - Impostazioni aziendali caricate:', settings);
          
          setCompanySettings(prev => ({
            ...prev,
            companyName: settings.companyName || 'FreeWheel',
            companyAddress: settings.companyAddress || '',
            companyCity: settings.companyCity || '',
            companyPostalCode: settings.companyPostalCode || '',
            companyProvince: settings.companyProvince || '',
            companyPhone: settings.companyPhone || '',
            companyEmail: settings.companyEmail || '',
            companyVatNumber: settings.companyVatNumber || ''
          }));
        }
      } catch (err) {
        console.error('Errore nel caricamento delle informazioni aziendali:', err);
        // Prova il secondo endpoint se il primo fallisce
        try {
          console.log('DEBUG CompleteSaleDialog - Tentativo alternativo con /settings');
          const response = await api.get('/settings');
          
          if (response.data) {
            const settings = {};
            
            // Mappa le impostazioni
            Object.entries(response.data).forEach(([key, value]) => {
              settings[key] = value;
            });
            
            console.log('DEBUG CompleteSaleDialog - Impostazioni aziendali caricate (seconda via):', settings);
            
            setCompanySettings(prev => ({
              ...prev,
              companyName: settings.companyName || 'FreeWheel',
              companyAddress: settings.companyAddress || '',
              companyCity: settings.companyCity || '',
              companyPostalCode: settings.companyPostalCode || '',
              companyProvince: settings.companyProvince || '',
              companyPhone: settings.companyPhone || '',
              companyEmail: settings.companyEmail || '',
              companyVatNumber: settings.companyVatNumber || ''
            }));
          }
        } catch (secondErr) {
          console.error('Errore anche nel secondo tentativo:', secondErr);
        }
      }
    };

    if (open) {
      fetchCompanySettings();
    }
  }, [open]);

  // Carica i metodi di pagamento e i conti
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [methodsRes, accountsRes] = await Promise.all([
          api.get('/payment-methods'),
          api.get('/accounts')
        ]);
        setPaymentMethods(methodsRes.data);
        setAccounts(accountsRes.data);
      } catch (err) {
        setError('Errore nel caricamento delle opzioni');
        console.error('Errore nel caricamento delle opzioni:', err);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  // Reset form quando il dialog si apre
  useEffect(() => {
    if (open && sale) {
      console.log('DEBUG CompleteSaleDialog - sale ricevuto da apertura dialog:', sale);
      
      // Calcolo corretto del totale
      const saleTotal = calculateTotal();
      console.log('DEBUG CompleteSaleDialog - Totale calcolato:', saleTotal);
      
      reset({
        paymentAmount: saleTotal,
        paymentDate: format(new Date(), 'yyyy-MM-dd'),
        paymentMethodId: '',
        accountId: ''
      });
      
      // Inizializza anche printData con gli stessi valori
      setPrintData({
        paymentAmount: saleTotal,
        paymentDate: format(new Date(), 'yyyy-MM-dd'),
        paymentMethodId: ''
      });
    }
  }, [open, sale, reset]);

  // Monitora i cambiamenti nei campi del form e aggiorna i dati per la stampa
  const updatePrintData = (name, value) => {
    setPrintData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      console.log('DEBUG CompleteSaleDialog - Dati form originali:', data);
      
      // Prepara i dati di vendita, gestendo correttamente gli articoli personalizzati
      const saleData = prepareSaleData();
      
      console.log('DEBUG CompleteSaleDialog - Dati vendita processati:', saleData);
      
      // Crea il payload da inviare all'API
      const payload = {
        customerId: saleData.customerId,
        items: saleData.items,
        notes: saleData.notes || '',
        payment: {
          amount: parseFloat(data.paymentAmount),
          paymentMethodId: parseInt(data.paymentMethodId),
          accountId: parseInt(data.accountId),
          date: data.paymentDate
        }
      };
      
      // Verifica che i dati siano validi prima dell'invio
      if (isNaN(payload.payment.amount) || payload.payment.amount <= 0) {
        setError("L'importo del pagamento deve essere maggiore di zero");
        setLoading(false);
        return;
      }
      
      if (isNaN(payload.payment.paymentMethodId) || payload.payment.paymentMethodId <= 0) {
        setError("Seleziona un metodo di pagamento valido");
        setLoading(false);
        return;
      }
      
      if (isNaN(payload.payment.accountId) || payload.payment.accountId <= 0) {
        setError("Seleziona un conto valido");
        setLoading(false);
        return;
      }
      
      if (!payload.payment.date) {
        setError("La data di pagamento è obbligatoria");
        setLoading(false);
        return;
      }
      
      console.log('DEBUG CompleteSaleDialog - Dati formattati per l\'invio:', payload);
      
      // Verifica che i dati della vendita siano validi
      if (!saleData.customerId) {
        setError("Dati vendita non validi");
        setLoading(false);
        return;
      }
      
      // Creiamo la vendita direttamente con status = completed
      // Modifichiamo la rotta per usare la vecchia rotta temporaneamente
      const response = await api.post('/sales', {
        ...payload,
        status: 'completed'  // Impostiamo direttamente lo stato a completed
      });
      console.log('DEBUG CompleteSaleDialog - Risposta dal server:', response.data);
      
      // Aggiorna il contesto e chiudi il dialog
      onCompleted();
      onClose();
    } catch (err) {
      console.error('Errore durante il completamento della vendita:', err);
      console.error('Dettagli risposta errore:', err.response?.data);
      
      let errorMessage = 'Errore durante il completamento della vendita';
      
      if (err.response) {
        // Gestione specifica per errori di disponibilità
        if (err.response.status === 400 && 
            (err.response.data?.message?.includes('insufficiente') || 
             err.response.data?.message?.includes('quantità') ||
             err.response.data?.msg?.includes('insufficiente') || 
             err.response.data?.msg?.includes('quantità'))) {
          errorMessage = 'Uno o più articoli non sono disponibili in magazzino';
        } else if (err.response.data?.msg) {
          errorMessage = err.response.data.msg;
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data?.errors && err.response.data.errors.length > 0) {
          errorMessage = err.response.data.errors[0].msg;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per stampa diretta
  const handleDirectPrint = () => {
    console.log('DEBUG CompleteSaleDialog - Stampa ricevuta con formValues:', control._formValues);
    console.log('DEBUG CompleteSaleDialog - Stampa ricevuta con printData:', printData);
    
    // Assicuriamoci che l'oggetto Customer sia correttamente incluso nei dati di vendita
    const saleWithCustomer = {
      ...sale,
      Customer: sale.customer || sale.Customer || null
    };
    
    // Stampa di debug dei dati Customer per verificare
    console.log('DEBUG CompleteSaleDialog - Customer data:', saleWithCustomer.Customer);

    // Preparare i dati della ricevuta
    const receiptData = {
      sale: {
        ...saleWithCustomer,
        SaleItems: sale?.SaleItems || sale?.items || [],
        total: calculateTotal()
      },
      paymentMethod: paymentMethods.find(m => m.id === Number(printData.paymentMethodId)),
      paymentAmount: parseFloat(printData.paymentAmount || 0),
      paymentDate: printData.paymentDate,
      companySettings
    };

    // Aprire la finestra di stampa
    const printWindow = window.open('', 'PRINT', 'width=600,height=800');
    
    // Generare il markup HTML in linea senza necessità di nascondere elementi
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Stampa Ricevuta</title>
          <meta charset="utf-8">
          <style>
            @page { 
              size: A5; 
              margin: 10mm;
            }
            body { 
              margin: 0; 
              padding: 0;
              font-family: Arial, Helvetica, sans-serif;
              color: #333;
            }
            .receipt-container {
              width: 148mm;
              padding: 10mm;
              box-sizing: border-box;
              background-color: white;
            }
            .receipt-page {
              width: 100%;
              page-break-after: always;
              break-inside: avoid;
              display: flex;
              flex-direction: column;
            }
            .receipt-page:last-child {
              page-break-after: auto;
            }
            .receipt-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 1.5mm;
            }
            .company-info {
              max-width: 100%;
            }
            .company-name {
              font-size: 14pt;
              font-weight: bold;
              color: #DB1111;
              margin-bottom: 1mm;
              word-break: break-word;
            }
            .company-details {
              font-size: 8pt;
              color: #666;
              line-height: 1.1;
            }
            .receipt-title {
              text-align: center;
              font-size: 13pt;
              font-weight: bold;
              margin: 1.5mm 0;
              margin-top: 15mm;
              color: #333;
              border-bottom: 2px solid #DB1111;
              padding-bottom: 0.5mm;
            }
            .customer-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 1.5mm;
              margin-bottom: 1.5mm;
              background-color: #f8f8f8;
              padding: 1.5mm;
              border-radius: 2px;
            }
            .info-label {
              font-size: 8pt;
              color: #666;
              margin-bottom: 0.5mm;
            }
            .info-value {
              font-weight: bold;
              font-size: 9pt;
            }
            .products-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9pt;
              margin-bottom: 4mm;
            }
            .products-table th, .products-table td {
              padding: 0.5mm 1mm;
              border-bottom: 1px solid #eee;
            }
            .products-table th {
              text-align: left;
            }
            .products-table td.price, .products-table th.price {
              text-align: right;
            }
            .products-table td.quantity, .products-table th.quantity {
              text-align: center;
            }
            .section-title {
              font-weight: bold;
              margin-bottom: 0.5mm;
              color: #DB1111;
              font-size: 10pt;
            }
            .payment-details {
              padding: 1.5mm;
              border: 1px solid #eee;
              border-radius: 2px;
              background-color: #f8f8f8;
              margin-bottom: 3mm;
            }
            .page-number {
              position: absolute;
              bottom: 1mm;
              right: 10mm;
              font-size: 8pt;
              color: #888;
            }
            .thank-you {
              width: 100%;
              text-align: center;
              margin-top: 5mm;
              font-size: 10pt;
              color: #333;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <!-- Contenuto dinamico della ricevuta -->
            ${generateReceiptHtml(receiptData)}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Dare al browser il tempo di rendere il contenuto prima di stampare
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Funzione per generare l'HTML della ricevuta
  const generateReceiptHtml = (data) => {
    const { sale, paymentMethod, paymentAmount, paymentDate, companySettings } = data;
    const items = sale?.SaleItems || sale?.items || [];
    const totalPages = Math.ceil(items.length / 16) || 1;
    
    // Formatta la data
    const formattedDate = paymentDate 
      ? new Date(paymentDate).toLocaleDateString('it-IT')
      : new Date().toLocaleDateString('it-IT');
      
    // Nome metodo di pagamento
    const paymentMethodName = paymentMethod?.name || 
      (sale?.PaymentMethod ? sale.PaymentMethod.name : 
        (sale?.paymentMethodId ? 'Metodo ID: ' + sale.paymentMethodId : '-'));
    
    let html = '';
    
    // Dividi gli articoli in pagine
    for (let i = 0; i < totalPages; i++) {
      const pageItems = items.slice(i * 16, (i + 1) * 16);
      const isLastPage = i === totalPages - 1;
      
      html += `
        <div class="receipt-page">
          <!-- Intestazione -->
          <div class="company-info">
            <div class="company-name">${companySettings.companyName || 'FreeWheel'}</div>
            <div class="company-details">
              ${companySettings.companyAddress || ''}
              ${companySettings.companyCity ? `<div>${companySettings.companyCity} ${companySettings.companyPostalCode || ''}</div>` : ''}
              ${companySettings.companyPhone ? `<div>Tel: ${companySettings.companyPhone}</div>` : ''}
              ${companySettings.companyEmail ? `<div>Email: ${companySettings.companyEmail}</div>` : ''}
              ${companySettings.companyVatNumber ? `<div>P.IVA: ${companySettings.companyVatNumber}</div>` : ''}
            </div>
          </div>
          
          <!-- Titolo -->
          <div class="receipt-title">RICEVUTA DI VENDITA</div>
          
          <!-- Info cliente -->
          <div class="customer-info">
            <div>
              <div class="info-label">Cliente</div>
              <div class="info-value">${sale?.Customer ? `${sale.Customer.firstName} ${sale.Customer.lastName}` : ''}</div>
            </div>
            <div>
              <div class="info-label">Telefono</div>
              <div class="info-value">${sale?.Customer?.phone || ''}</div>
            </div>
            <div>
              <div class="info-label">Data Pagamento</div>
              <div class="info-value">${formattedDate}</div>
            </div>
            <div>
              <div class="info-label">Numero Ricevuta</div>
              <div class="info-value">${getReceiptNumber()}</div>
            </div>
          </div>
          
          <!-- Articoli -->
          ${pageItems.length > 0 ? `
            <div>
              <div class="section-title">PRODOTTI ACQUISTATI</div>
              <table class="products-table">
                <thead>
                  <tr>
                    <th>Prodotto</th>
                    <th class="quantity">Quantità</th>
                    <th class="price">Prezzo Unitario</th>
                    <th class="price">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  ${pageItems.map((item, idx) => `
                    <tr>
                      <td>${item.description || 'Senza descrizione'}</td>
                      <td class="quantity">${item.quantity}</td>
                      <td class="price">€${formatAmount(item.unitPrice || item.price || 0)}</td>
                      <td class="price">€${formatAmount((item.unitPrice || item.price || 0) * item.quantity)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}
          
          <!-- Dettagli pagamento solo nell'ultima pagina -->
          ${isLastPage ? `
            <div>
              <div class="section-title">DETTAGLI PAGAMENTO</div>
              <div class="payment-details">
                <table class="products-table">
                  <tbody>
                    <tr>
                      <td>Totale vendita</td>
                      <td class="price">€${formatAmount(calculateTotal())}</td>
                    </tr>
                    <tr>
                      <td>Importo pagato</td>
                      <td class="price">€${formatAmount(paymentAmount)}</td>
                    </tr>
                    <tr>
                      <td>Metodo di pagamento</td>
                      <td class="price">${paymentMethodName}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ` : ''}
          
          <!-- Numero pagina -->
          <div class="page-number">Pagina ${i + 1} / ${totalPages}</div>
          
          <!-- Ringraziamento solo nell'ultima pagina -->
          ${isLastPage ? `
            <div class="thank-you">
              Grazie per aver scelto i nostri servizi!<br>
              Lo staff di ${companySettings.companyName || 'FreeWheel'}
            </div>
          ` : ''}
        </div>
      `;
    }
    
    return html;
  };

  // Calcolo del totale da visualizzare
  const displayTotal = calculateTotal();

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Concludi vendita #{sale?.id}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Totale vendita"
                  value={formatAmount(displayTotal)}
                  fullWidth
                  disabled
                  InputProps={{
                    readOnly: true,
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="paymentAmount"
                  control={control}
                  rules={{ 
                    required: 'Importo richiesto',
                    min: { value: 0.01, message: 'L\'importo deve essere maggiore di 0' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => {
                        field.onChange(e); // Per react-hook-form
                        updatePrintData('paymentAmount', e.target.value); // Per i dati di stampa
                      }}
                      label="Importo pagato"
                      type="number"
                      fullWidth
                      error={!!errors.paymentAmount}
                      helperText={errors.paymentAmount?.message}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="paymentDate"
                  control={control}
                  rules={{ required: 'Data pagamento richiesta' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => {
                        field.onChange(e); // Per react-hook-form
                        updatePrintData('paymentDate', e.target.value); // Per i dati di stampa
                      }}
                      label="Data pagamento"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.paymentDate}
                      helperText={errors.paymentDate?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="paymentMethodId"
                  control={control}
                  rules={{ required: 'Metodo di pagamento richiesto' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => {
                        field.onChange(e); // Per react-hook-form
                        updatePrintData('paymentMethodId', e.target.value); // Per i dati di stampa
                      }}
                      select
                      label="Metodo di pagamento"
                      fullWidth
                      error={!!errors.paymentMethodId}
                      helperText={errors.paymentMethodId?.message}
                    >
                      {paymentMethods.map((method) => (
                        <MenuItem key={method.id} value={method.id}>
                          {method.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="accountId"
                  control={control}
                  rules={{ required: 'Conto richiesto' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Conto"
                      fullWidth
                      error={!!errors.accountId}
                      helperText={errors.accountId?.message}
                    >
                      {accounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDirectPrint} disabled={loading} startIcon={<PrintIcon />}>
              Stampa Ricevuta
            </Button>
            <Button onClick={onClose} disabled={loading}>
              Annulla
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              {loading ? 'Completamento...' : 'Completa vendita'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Ricevuta di vendita nascosta per la stampa */}
      <div id="sale-receipt-print" style={{ display: 'none' }}>
        <SaleReceiptPrint
          sale={{
            ...sale,
            SaleItems: sale?.SaleItems || sale?.items || [],
            total: calculateTotal()
          }}
          paymentMethod={paymentMethods.find(m => m.id === Number(printData.paymentMethodId))}
          paymentAmount={parseFloat(printData.paymentAmount || 0)}
          paymentDate={printData.paymentDate}
        />
      </div>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompleteSaleDialog; 